import type {
  CatalogResponse,
  ItemDetailResponse,
  ItemHoldersResponse,
  LeaderboardResponse,
  PlayerInventoryResponse,
  PlayerProfileResponse,
} from './types';

const API_BASE = (import.meta.env.VITE_API_URL || 'https://api.rolantir.com/').replace(/\/$/, '');
const API_KEY_STORAGE = 'rolantir_api_key';

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

function getStoredApiKey(): string {
  return localStorage.getItem(API_KEY_STORAGE) || '';
}

async function apiGet<T>(path: string): Promise<T> {
  const apiKey = getStoredApiKey();
  if (!apiKey) {
    throw new ApiError('Missing API key', 401);
  }

  const response = await fetch(`${API_BASE}${path}`, {
    headers: {
      'x-api-key': apiKey,
    },
  });

  if (!response.ok) {
    const message = await response.text();
    throw new ApiError(message || `Request failed with ${response.status}`, response.status);
  }
  return response.json() as Promise<T>;
}

export const api = {
  setApiKey(value: string) {
    localStorage.setItem(API_KEY_STORAGE, value);
  },
  clearApiKey() {
    localStorage.removeItem(API_KEY_STORAGE);
  },
  getApiKey() {
    return getStoredApiKey();
  },
  async validateApiKey(candidate: string): Promise<boolean> {
    const response = await fetch(`${API_BASE}/api/ftf/version`, {
      headers: {
        'x-api-key': candidate,
      },
    });
    return response.ok;
  },
  getCatalog(query: URLSearchParams) {
    return apiGet<CatalogResponse>(`/api/ftf/items?${query.toString()}`);
  },
  getItem(itemKey: string) {
    return apiGet<ItemDetailResponse>(`/api/ftf/items/${encodeURIComponent(itemKey)}`);
  },
  getItemHolders(itemKey: string, offset: number = 0, limit: number = 10) {
    return apiGet<ItemHoldersResponse>(`/api/ftf/item-holders?key=${encodeURIComponent(itemKey)}&offset=${offset}&limit=${limit}`);
  },
  getItemRecent(itemKey: string, offset: number = 0, limit: number = 10) {
    return apiGet<ItemHoldersResponse>(`/api/ftf/item-recent?key=${encodeURIComponent(itemKey)}&offset=${offset}&limit=${limit}`);
  },
  getLeaderboard(limit: number = 25, page: number = 1) {
    return apiGet<LeaderboardResponse>(`/api/ftf/leaderboard?page=${page}&limit=${limit}`);
  },
  getPlayer(robloxId: string) {
    return apiGet<PlayerProfileResponse>(`/api/ftf/players/${encodeURIComponent(robloxId)}`);
  },
  getPlayerInventory(robloxId: string, params: {
    search?: string;
    type?: string;
    rarity?: string;
    sort?: string;
    page?: number;
    limit?: number;
  }) {
    const q = new URLSearchParams();
    if (params.search)  q.set('search', params.search);
    if (params.type)    q.set('type',   params.type);
    if (params.rarity)  q.set('rarity', params.rarity);
    if (params.sort)    q.set('sort',   params.sort);
    if (params.page)    q.set('page',   String(params.page));
    if (params.limit)   q.set('limit',  String(params.limit));
    return apiGet<PlayerInventoryResponse>(`/api/ftf/players/${encodeURIComponent(robloxId)}/inventory?${q.toString()}`);
  },
};
